import React from "react";
import { Collapse } from "antd";
import { Field } from "redux-form";
import { components } from "react-select";

import { customInput } from "../../../../../../../../common/custom-input";
import { CustomSingleSelect } from "../../../../../../../../common/new-core-ui/CustomSingleSelect";
import styles from "../../../../../../JobStyle.module.css";
import v2Styles from "../../../../../../../v2Styles.module.css";
import FormSelectStyle from "../../../../../../../v2FormSelectStyle";
import emergencyStyles from "../../JobSwmsV3EmergencyPlansContainer.module.css";

const { Panel } = Collapse;

const getStatusColor = (status) => {
  switch (`${status || "DRAFT"}`.toUpperCase()) {
    case "APPROVED":
      return { bg: "#D1FAE5", color: "#065F46", text: "Approved" };
    case "PENDING_APPROVAL":
      return { bg: "#DBEAFE", color: "#1E40AF", text: "Pending Approval" };
    case "REJECTED":
      return { bg: "#FEE2E2", color: "#991B1B", text: "Rejected" };
    case "DRAFT":
    default:
      return { bg: "#FEF3C7", color: "#92400E", text: "Draft" };
  }
};

const CategorySelectField = ({ input, meta, options, ...props }) => (
  <CustomSingleSelect
    {...props}
    input={input}
    meta={meta}
    options={options}
    value={input.value || null}
  />
);

export default function JobSwmsV3EmergencyPlanDetailsSection({
  planIndex,
  categories = [],
  documentNumber,
  swmsStatus,
  swmsData = {},
  loading
}) {
  const categoryOptions = categories.map((category) => ({
    label: category.name || category.category_name,
    value: category.id
  }));
  const statusStyle = getStatusColor(swmsStatus);
  const documentProjectDetails = swmsData
    && swmsData.sections
    && swmsData.sections.document_project_details
    && swmsData.sections.document_project_details.data
    ? swmsData.sections.document_project_details.data
    : {};
  const scopeOfWork = documentProjectDetails.scope_of_work || {};
  const qliAreas = documentProjectDetails.qli_areas || {};
  const areas = documentProjectDetails.areas || {};
  const showQliAreas = Number(qliAreas.toggle) === 1;
  const showAreas = Number(areas.toggle) === 1;
  const renderValueChips = (values = []) => (
    <div className={`${emergencyStyles.ValueChipContainer} d-flex`}>
      {(Array.isArray(values) ? values : []).map((item) => (
        <span className={emergencyStyles.ValueChip} key={typeof item === "string" ? item : (item.id || item.name)}>
          {typeof item === "string" ? item : item.name}
        </span>
      ))}
    </div>
  );

  const panel = (
    <Panel
      key="emergency-plan-details"
      className="custom-job-swmsv3-collapse-panel"
      header={
        <div
          className={`${styles.BackgroundSectionHeading} custom-section-v2-panel-heading`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer"
          }}
        >
          <span aria-hidden="true" style={{ display: "inline-flex", color: "#362148" }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="14" height="14">
              <path
                fill="currentColor"
                d="M256 160L256 224L384 224L384 160C384 124.7 355.3 96 320 96C284.7 96 256 124.7 256 160zM192 224L192 160C192 89.3 249.3 32 320 32C390.7 32 448 89.3 448 160L448 224C483.3 224 512 252.7 512 288L512 512C512 547.3 483.3 576 448 576L192 576C156.7 576 128 547.3 128 512L128 288C128 252.7 156.7 224 192 224z"
              />
            </svg>
          </span>
          Emergency Plan Details
        </div>
      }
    >
        <div className="container-fluid my-2 pb-2">
          <div className="row">
            <div className="col-6">
              <div className={styles.Label}>Plan Name</div>
              <Field
                name={`plans[${planIndex}].plan_name`}
                className={v2Styles.TextField}
                component={customInput}
                disabled={loading}
              />
            </div>
            <div className="col-6">
              <div className={styles.Label}>Rescue Category</div>
              <Field
                name={`plans[${planIndex}].categories`}
                component={CategorySelectField}
                options={categoryOptions}
                styles={FormSelectStyle}
                otherProps={{
                  isDisabled: loading,
                  isLoading: loading,
                  isMulti: true,
                  closeMenuOnSelect: false,
                  hideSelectedOptions: false,
                  components: { dropdownIndicator: <components.DownChevron /> }
                }}
              />
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-6">
              <div className={styles.Label}>Emergency Plan ID</div>
              <input
                className={v2Styles.TextField}
                value={documentNumber || ""}
                disabled
                readOnly
              />
            </div>
            <div className="col-3">
              <div className={styles.Label}>Version</div>
              <input
                className={v2Styles.TextField}
                value={documentProjectDetails.swms_version || ""}
                disabled
                readOnly
              />
            </div>
            <div className="col-3">
              <div className={styles.Label}>Status</div>
              <div className="d-flex align-items-center pt-1" style={{ gap: "10px" }}>
                <div
                  className="swms-info-status-badge"
                  style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}
                >
                  {statusStyle.text}
                </div>
                <div className="swms-info-auto-updated-label">Auto-updated by workflow</div>
              </div>
            </div>
          </div>
          <hr className="swms-info-section-divider" />

          <div className="row">
            <div className="col-12">
              <div className={styles.Label} style={{ fontSize: "14px" }}>Work Context — from SWMS</div>
            </div>
          </div>
          
          <div className="row mt-2">
            <div className="col-6">
              <div className={styles.Label}>SWMS Name</div>
              <input
                className={v2Styles.TextField}
                value={documentProjectDetails.swms_name || ""}
                disabled
                readOnly
              />
            </div>
            <div className="col-6">
              <div className={styles.Label}>Site Name</div>
              <input
                className={v2Styles.TextField}
                value={documentProjectDetails.site_name || ""}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-12">
              <div className={styles.Label}>Site Address</div>
              <input
                className={v2Styles.TextField}
                value={documentProjectDetails.site_address || ""}
                disabled
                readOnly
              />
            </div>
          </div>
          {Number(scopeOfWork.toggle) === 1 ? (
            <div className="row mt-2">
              <div className="col-12">
                <div className={styles.Label}>Scope of Work</div>
                <input
                  className={v2Styles.TextField}
                  value={scopeOfWork.value || ""}
                  disabled
                  readOnly
                />
              </div>
            </div>
          ) : null}
          {showQliAreas || showAreas ? (
            <div className="row mt-2">
              {showQliAreas ? (
                <div className="col-6">
                  <div className={styles.Label}>QLI Areas</div>
                  {renderValueChips(qliAreas.value)}
                </div>
              ) : null}
              {showAreas ? (
                <div className="col-6">
                  <div className={styles.Label}>Areas</div>
                  {renderValueChips(areas.values)}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
    </Panel>
  );

  return panel;
}
